// Environment specific values this package needs at runtime. The host application owns
// its environment, so it injects these once at bootstrap and `@acre/utils` imports no
// environment module of its own — which is what keeps other environments' hosts and
// credentials out of the published bundle.
export interface UtilsConfig {
  /** Endpoint serving the public key general insurance credentials are encrypted with. */
  generalInsuranceRsaPublicKeyUrl: string
  /** Endpoint serving the public key protection credentials are encrypted with. */
  protectionRsaPublicKeyUrl: string
  /** Endpoint serving the public key lender application credentials are encrypted with. */
  applicationRsaPublicKeyUrl: string
  /** Endpoint that exchanges an expired session cookie for a fresh one. */
  refreshUrl: string
  /** Organisation id of the client originator. */
  clientOriginator: string
  /** Comma separated list of feature flags enabled for the whole deployment. */
  globalFeatureFlags: string
}

// Keys with no meaningful absent value. Reading one before it is set is a defect — the
// alternative is silently talking to the wrong host — so it throws rather than defaults.
type RequiredConfigKey = Exclude<keyof UtilsConfig, 'globalFeatureFlags'>

let injectedConfig: Readonly<UtilsConfig> | undefined

/**
 * Injects the runtime configuration. Call once from the host application's entry point,
 * before anything renders or issues a request.
 */
export const setUtilsConfig = (config: UtilsConfig) => {
  injectedConfig = Object.freeze({ ...config })
}

const readRequired = (key: RequiredConfigKey) => {
  const value = injectedConfig?.[key]

  if (!value) {
    throw new Error(
      `@acre/utils has no runtime configuration for "${key}". Call setUtilsConfig() with the host application's environment configuration from its entry point before this code runs.`,
    )
  }

  return value
}

export const getGeneralInsuranceRsaPublicKeyUrl = () => readRequired('generalInsuranceRsaPublicKeyUrl')

export const getProtectionRsaPublicKeyUrl = () => readRequired('protectionRsaPublicKeyUrl')

export const getApplicationRsaPublicKeyUrl = () => readRequired('applicationRsaPublicKeyUrl')

export const getRefreshUrl = () => readRequired('refreshUrl')

export const getClientOriginator = () => readRequired('clientOriginator')

// A deployment that enables no flags globally is a valid state — every flag keeps its
// default — so an absent value reads as empty rather than throwing.
export const getGlobalFeatureFlags = () => injectedConfig?.globalFeatureFlags ?? ''
